import { useEffect, useRef } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import styles from './MarkupPreview.module.css';

const markupPreview = cva(styles.host);

export interface MarkupPreviewProps {
  /** HTML or SVG string to render. Must be trusted — sanitize at the producer. */
  markup: string;
  /** Scale factor 0..1. Default 0.45. */
  scale?: number;
  className?: string;
}

// Styles injected inside the shadow root so host-page CSS never leaks through
// and document-emitted class names don't collide with anything on the page.
const SHADOW_STYLES = `
  :host { all: initial; display: block; width: 100%; height: 100%; contain: strict; pointer-events: none; user-select: none; }
  .inner.html-kind {
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% / var(--s, 0.45));
    height: calc(100% / var(--s, 0.45));
    transform: scale(var(--s, 0.45));
    transform-origin: top left;
    overflow: hidden;
    padding: var(--space-5) var(--space-6);
    box-sizing: border-box;
    user-select: none;
    background: var(--preview-bg, #ffffff);
    color: var(--preview-fg, #111111);
  }
  .inner.html-kind > * { max-width: 100%; }
  .inner.html-kind img, .inner.html-kind svg { max-width: 100%; }
  .inner.html-kind table {
    border-collapse: separate;
    border-spacing: 0;
    width: calc(100% - var(--space-2, 8px));
    margin: var(--space-5, 20px) var(--space-1, 4px);
    font-size: var(--text-sm, 13px);
    table-layout: fixed;
    background: color-mix(in srgb, var(--preview-bg, #ffffff) 40%, transparent);
    border: 1px solid color-mix(in srgb, var(--preview-fg, #111111) 8%, transparent);
    border-radius: 12px;
    overflow: hidden;
  }
  .inner.html-kind td {
    border-bottom: 1px solid color-mix(in srgb, var(--preview-fg, #111111) 6%, transparent);
    padding: var(--space-3, 12px) var(--space-4, 16px);
    vertical-align: top;
    font-size: var(--text-base, 15px);
  }
  .inner.html-kind th {
    border-bottom: 1px solid color-mix(in srgb, var(--preview-fg, #111111) 10%, transparent);
    padding: var(--space-3, 12px) var(--space-4, 16px);
    font-weight: 600;
    text-align: left;
    font-size: var(--text-sm, 13px);
    color: color-mix(in srgb, var(--preview-fg, #111111) 60%, transparent);
    background: color-mix(in srgb, var(--preview-bg, #ffffff) 60%, transparent);
  }
  .inner.html-kind tr:last-child td { border-bottom: none; }
  .inner.html-kind td:last-child, .inner.html-kind th:last-child { border-right: none; }
  .inner.svg-kind {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-2);
    box-sizing: border-box;
  }
  .inner.svg-kind > svg {
    width: 100% !important;
    height: 100% !important;
    max-width: 100%;
    max-height: 100%;
  }
`;

/**
 * MarkupPreview — scaled-down rendering of an HTML or SVG string with
 * style isolation via shadow DOM.
 *
 * The producer (upstream of this component) is responsible for sanitizing
 * the markup before passing it in. Scripts embedded in markup will not
 * execute because innerHTML doesn't execute <script>, but the caller should
 * still run untrusted content through DOMPurify or similar.
 */
export function MarkupPreview({ markup, scale = 0.45, className }: MarkupPreviewProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

    while (shadow.firstChild) shadow.removeChild(shadow.firstChild);

    if (!markup) return;

    const styleEl = document.createElement('style');
    styleEl.textContent = SHADOW_STYLES;
    shadow.appendChild(styleEl);

    const inner = document.createElement('div');
    const isSvg = markup.trimStart().startsWith('<svg');
    inner.className = isSvg ? 'inner svg-kind' : 'inner html-kind';
    inner.style.setProperty('--s', String(scale));

    if (isSvg) {
      // Parse the SVG into an actual SVG document tree and import the root
      // element into this document. Assigning via innerHTML treats the SVG
      // as HTML and can mis-parse namespaced attributes; DOMParser produces
      // a proper SVGElement that the browser renders reliably.
      const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
      const parsedRoot = parsed.documentElement;
      if (parsedRoot && parsedRoot.tagName.toLowerCase() === 'svg') {
        inner.appendChild(document.importNode(parsedRoot, true));
      } else {
        inner.innerHTML = markup;
      }
    } else {
      inner.innerHTML = markup;
    }
    shadow.appendChild(inner);
  }, [markup, scale]);

  return (
    <div
      ref={hostRef}
      data-markup-host
      className={clsx(markupPreview(), className)}
      aria-hidden="true"
    />
  );
}
